import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseView, BaseText } from '@/components/base'
import { CardItem } from '@/components/CardItem'
import { ChevronRight } from '@/assets/icons'
import { useTheme } from '@/hooks/useTheme'
import { formatAddress } from '@/utils'

interface ExternalWalletAddress {
  name?: string
  address: string
}

interface AddressSelectorProps {
  externalWalletAddress?: ExternalWalletAddress
  isLoading?: boolean
  blockchainCode?: string
  savedAddressesCount?: number // Количество сохраненных адресов
  isLoadingAddresses?: boolean // Загружаются ли адреса
}

export const AddressSelector: React.FC<AddressSelectorProps> = ({
  externalWalletAddress,
  isLoading = false,
  savedAddressesCount = 0,
  isLoadingAddresses = false,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()

  const hasAddress = !!externalWalletAddress?.address

  const handleRedirectToRegisterAddress = () => {
    // Если данные еще загружаются, не делаем ничего
    if (isLoadingAddresses) {
      return
    }

    // Если есть сохраненные адреса, идем на страницу выбора, иначе на регистрацию нового
    const route =
      savedAddressesCount > 0
        ? '/withdraw/choose-address'
        : '/withdraw/register-wallet-address'

    navigate(route)
  }

  if (isLoading) {
    return (
      <BaseView
        p={16}
        rounded={12}
        style={{
          backgroundColor: theme.colors.background.cardItemRoot,
          height: '66px',
        }}
      >
        <BaseView
          style={{
            height: '20px',
            backgroundColor: '#374151',
            borderRadius: '4px',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </BaseView>
    )
  }

  return (
    <CardItem.Root
      style={{
        padding: hasAddress ? '13px 16px' : '21px 16px',
        cursor: 'pointer',
      }}
      onPress={handleRedirectToRegisterAddress}
    >
      {!hasAddress && (
        <CardItem.Content>
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
          >
            {t('translation:selectOrAddNewAddress')}
          </BaseText>
        </CardItem.Content>
      )}

      {hasAddress && (
        <CardItem.Content>
          <BaseView gap={4}>
            {externalWalletAddress.name && (
              <BaseText
                fontSize={16}
                fontFamily="semibold"
                color="label.default"
              >
                {externalWalletAddress.name}
              </BaseText>
            )}
            <BaseText
              fontSize={16}
              fontFamily="regular"
              color="label.default"
              style={{
                fontFamily: 'monospace',
              }}
            >
              {formatAddress(externalWalletAddress.address)}
            </BaseText>
          </BaseView>
        </CardItem.Content>
      )}

      <CardItem.RightIcon>
        <ChevronRight size={24} color={theme.colors.icon?.iconCard || '#666'} />
      </CardItem.RightIcon>
    </CardItem.Root>
  )
}
